import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { firstValueFrom } from 'rxjs';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GRC_INDUSTRY_PACK_KEYS } from '../grc/grc-form.models';
import {
  PlatformAdminService,
  ProvisionStepResult,
  ProvisionTenantRequest,
  ProvisionTenantResponse
} from './platform-admin.service';

interface SelectOption<T = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

interface ChecklistItem {
  key: string;
  status: string;
  error: string;
}

@Component({
  standalone: true,
  selector: 'ain-platform-tenant-provision',
  providers: [ConfirmationService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ConfirmDialogModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="pageTitle()"
      [description]="pageDescription()">
      <div page-actions>
        <a pButton severity="secondary" [routerLink]="['/platform/tenants']" [label]="t('platform.tenants.provision.backToList')"></a>
      </div>

      <p-confirmDialog />

      <ain-info-hint
        [titleKey]="hintTitleKey()"
        [descriptionKey]="hintDescriptionKey()"
        [bulletKeys]="hintBulletKeys()" />

      @if (error()) {
        <ain-state type="error" [title]="t('platform.tenants.provision.errorTitle')" [message]="error() ?? ''" />
      }

      @if (checklist().length) {
        <section class="checklist-panel" [class.failed]="hasFailed()" [class.complete]="allSucceeded()">
          <h3>{{ checklistTitle() }}</h3>
          <ol class="checklist">
            @for (step of checklist(); track step.key) {
              <li [class]="'is-' + step.status">
                <span class="mark" [attr.aria-label]="stepStatusLabel(step.status)">
                  @switch (step.status) {
                    @case ('succeeded') { <i class="pi pi-check" aria-hidden="true"></i> }
                    @case ('failed') { <i class="pi pi-times" aria-hidden="true"></i> }
                    @case ('running') { <i class="pi pi-spin pi-spinner" aria-hidden="true"></i> }
                    @default { <i class="pi pi-circle" aria-hidden="true"></i> }
                  }
                </span>
                <div class="step-body">
                  <strong>{{ t('platform.tenants.provision.step.' + step.key) }}</strong>
                  @if (step.error) {
                    <p class="step-error">{{ step.error }}</p>
                  }
                </div>
              </li>
            }
          </ol>

          @if (hasFailed() && !busy()) {
            <div class="form-actions">
              <button pButton type="button" [label]="t('platform.tenants.provision.retryFailed')" (click)="retryFailed()"></button>
              <button
                pButton
                type="button"
                severity="danger"
                [label]="t('platform.tenants.provision.discardDraft')"
                [disabled]="!result()?.tenantId"
                (click)="confirmDiscard()"></button>
            </div>
          }
        </section>
      }

      @if (allSucceeded() && result()) {
        <section class="success-panel">
          <h3>{{ result()!.resumed ? t('platform.tenants.provision.success.resumedTitle') : successTitle() }}</h3>
          <div class="success-grid">
            <div><strong>{{ t('platform.tenants.name') }}:</strong> {{ result()!.name }}</div>
            <div><strong>{{ t('platform.tenants.slug') }}:</strong> {{ result()!.slug }}</div>
            <div><strong>{{ t('platform.tenants.provision.success.tenantId') }}:</strong> {{ result()!.tenantId }}</div>
            <div><strong>{{ t('platform.tenants.provision.adminEmail') }}:</strong> {{ result()!.adminEmail }}</div>
            <div><strong>{{ t('platform.tenants.provision.adminDisplayName') }}:</strong> {{ result()!.adminDisplayName }}</div>
          </div>

          @if (temporaryPassword()) {
            <label class="password-field">
              <span>{{ t('platform.tenants.provision.success.temporaryPassword') }}</span>
              <div class="password-row">
                <input pInputText readonly [value]="temporaryPassword()" class="password-input" />
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [label]="passwordCopied() ? t('platform.tenants.provision.copyPasswordDone') : t('platform.tenants.provision.copyPassword')"
                  (click)="copyPassword()"></button>
              </div>
            </label>
            <p class="warning">{{ t('platform.tenants.provision.success.passwordWarning') }}</p>
          } @else {
            <p class="warning">{{ t('platform.tenants.provision.success.adminAlreadyExists') }}</p>
          }

          @if (result()!.entitlements?.length) {
            <div>
              <strong>{{ t('platform.tenants.provision.success.entitlements') }}:</strong>
              <ul class="entitlements">
                @for (item of result()!.entitlements; track entitlementTrackKey(item)) {
                  <li><code>{{ formatEntitlement(item) }}</code></li>
                }
              </ul>
            </div>
          }

          <div class="flags">
            <span>{{ t('platform.tenants.provision.success.packActivated') }}: {{ yesNo(result()!.packActivated) }}</span>
            <span>{{ t('platform.tenants.provision.success.seeded') }}: {{ yesNo(result()!.seeded) }}</span>
          </div>

          <a pButton [routerLink]="['/platform/tenants', result()!.tenantId]" [label]="t('platform.tenants.provision.success.openTenant')"></a>
        </section>
      } @else if (!checklist().length || hasFailed()) {
        <form class="provision-form" (ngSubmit)="submit()">
          <label>
            <span>{{ t('platform.tenants.provision.subsystem') }}</span>
            <p-select
              [options]="subsystemOptions()"
              optionLabel="label"
              optionValue="value"
              optionDisabled="disabled"
              [(ngModel)]="model.subsystemKey"
              name="subsystemKey"
              appendTo="body" />
          </label>

          <label>
            <span>{{ t('platform.tenants.name') }}</span>
            <input pInputText [(ngModel)]="model.name" name="name" required />
          </label>

          <label>
            <span>{{ t('platform.tenants.slug') }}</span>
            <input pInputText [(ngModel)]="model.slug" name="slug" required />
          </label>

          <label>
            <span>{{ t('platform.tenants.legalName') }}</span>
            <input pInputText [(ngModel)]="model.legalName" name="legalName" required />
          </label>

          @if (isStorefrontTenant()) {
            <label>
              <span>{{ t('platform.tenants.host') }}</span>
              <input pInputText [(ngModel)]="model.host" name="host" required placeholder="example.com" />
            </label>

            <label>
              <span>{{ t('platform.tenants.storeName') }}</span>
              <input pInputText [(ngModel)]="model.storeName" name="storeName" />
            </label>

            <label>
              <span>{{ t('platform.tenants.provision.storeKey') }}</span>
              <input pInputText [(ngModel)]="model.storeKey" name="storeKey" [placeholder]="model.slug || 'brand'" />
            </label>

            <label>
              <span>{{ t('platform.tenants.provision.defaultCurrency') }}</span>
              <input pInputText [(ngModel)]="model.defaultCurrency" name="defaultCurrency" required />
            </label>
          }

          <label>
            <span>{{ t('platform.tenants.defaultCulture') }}</span>
            <input pInputText [(ngModel)]="model.defaultCulture" name="defaultCulture" required />
          </label>

          <label>
            <span>{{ t('platform.tenants.defaultTimeZone') }}</span>
            <input pInputText [(ngModel)]="model.defaultTimeZone" name="defaultTimeZone" required />
          </label>

          <label>
            <span>{{ t('platform.tenants.defaultCalendarSystem') }}</span>
            <p-select
              [options]="calendarOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="model.defaultCalendarSystem"
              name="defaultCalendarSystem"
              appendTo="body" />
          </label>

          @if (!isStorefrontTenant()) {
            <label>
              <span>{{ t('platform.tenants.industryPack') }}</span>
              <p-select
                [options]="industryPackOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.industryPackKey"
                name="industryPackKey"
                appendTo="body" />
            </label>
          }

          <label>
            <span>{{ t('platform.tenants.provision.planCode') }}</span>
            <p-select
              [options]="planOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="model.planCode"
              name="planCode"
              appendTo="body" />
          </label>

          <label>
            <span>{{ t('platform.tenants.provision.adminEmail') }}</span>
            <input pInputText type="email" [(ngModel)]="model.adminEmail" name="adminEmail" required />
          </label>

          <label>
            <span>{{ t('platform.tenants.provision.adminDisplayName') }}</span>
            <input pInputText [(ngModel)]="model.adminDisplayName" name="adminDisplayName" required />
          </label>

          @if (isStorefrontTenant()) {
            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="model.enablePaymentsProvider" name="enablePaymentsProvider" inputId="enablePaymentsProvider" />
              <span>{{ t('platform.tenants.provision.enablePaymentsProvider') }}</span>
            </label>
          } @else {
            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="model.activateIndustryPack" name="activateIndustryPack" inputId="activateIndustryPack" />
              <span>{{ t('platform.tenants.provision.activateIndustryPack') }}</span>
            </label>

            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="model.seedDemoData" name="seedDemoData" inputId="seedDemoData" />
              <span>{{ t('platform.tenants.provision.seedDemoData') }}</span>
            </label>

            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="model.seedFccDemo" name="seedFccDemo" inputId="seedFccDemo" />
              <span>{{ t('platform.tenants.provision.seedFccDemo') }}</span>
            </label>
          }

          <div class="form-actions">
            <button pButton type="submit" [label]="t('platform.tenants.provision.submit')" [disabled]="busy() || !canSubmit()"></button>
          </div>
        </form>
      }
    </ain-page>
  `,
  styles: [`
    .provision-form {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-top: 16px;
    }
    .provision-form label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;
    }
    .provision-form label > span {
      white-space: normal;
      word-break: break-word;
      line-height: 1.4;
      color: #334155;
      font-weight: 600;
    }
    .checkbox-row {
      flex-direction: row !important;
      align-items: flex-start;
      gap: 8px !important;
      grid-column: 1 / -1;
    }
    .checkbox-row span {
      font-weight: 500 !important;
    }
    .form-actions {
      grid-column: 1 / -1;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .checklist-panel,
    .success-panel {
      margin-top: 16px;
      display: grid;
      gap: 12px;
      padding: 16px;
      border: 1px solid #cbd5e1;
      border-radius: 12px;
      background: #f8fafc;
    }
    .checklist-panel.failed {
      border-color: #fca5a5;
      background: #fef2f2;
    }
    .checklist-panel.complete {
      border-color: #86efac;
      background: #f0fdf4;
    }
    .checklist {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 8px;
    }
    .checklist li {
      display: flex;
      gap: 10px;
      align-items: flex-start;
    }
    .mark {
      width: 1.5rem;
      height: 1.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }
    .is-succeeded .mark { color: #15803d; }
    .is-failed .mark { color: #b91c1c; }
    .is-running .mark { color: #0369a1; }
    .is-pending .mark { color: #94a3b8; }
    .step-body strong,
    .success-grid strong,
    .flags span,
    .warning,
    .step-error {
      white-space: normal;
      word-break: break-word;
    }
    .step-error {
      margin: 4px 0 0;
      color: #b91c1c;
    }
    .success-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .password-field span {
      display: block;
      margin-bottom: 6px;
      font-weight: 600;
      color: #334155;
    }
    .password-row {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      align-items: center;
    }
    .password-input {
      flex: 1 1 16rem;
      min-width: 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }
    .warning {
      margin: 0;
      color: #b45309;
      background: #fffbeb;
      border: 1px solid #fcd34d;
      border-radius: 8px;
      padding: 10px 12px;
    }
    .entitlements {
      margin: 6px 0 0;
      padding-inline-start: 1.25rem;
    }
    .flags {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    @media (max-width: 1200px) {
      .provision-form,
      .success-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class PlatformTenantProvisionComponent {
  private readonly api = inject(PlatformAdminService);
  private readonly localizationService = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly result = signal<ProvisionTenantResponse | null>(null);
  readonly checklist = signal<ChecklistItem[]>([]);
  readonly passwordCopied = signal(false);
  readonly temporaryPassword = signal('');

  readonly hasFailed = computed(() => this.checklist().some(item => item.status === 'failed'));
  readonly allSucceeded = computed(() => {
    const items = this.checklist();
    return items.length > 0 && items.every(item => item.status === 'succeeded');
  });

  model = {
    subsystemKey: 'marketplace',
    name: '',
    slug: '',
    legalName: '',
    host: '',
    storeName: '',
    storeKey: '',
    defaultCurrency: 'IRR',
    defaultCulture: 'fa-IR',
    defaultTimeZone: 'Asia/Tehran',
    defaultCalendarSystem: 'jalali' as 'gregorian' | 'jalali',
    industryPackKey: 'manufacturing',
    planCode: 'trial-30',
    adminEmail: '',
    adminDisplayName: '',
    activateIndustryPack: true,
    seedDemoData: false,
    seedFccDemo: false,
    enablePaymentsProvider: true
  };

  readonly subsystemOptions = computed<SelectOption[]>(() => [
    { value: 'marketplace', label: this.t('platform.tenants.provision.subsystem.marketplace'), disabled: false },
    { value: 'software', label: this.t('platform.tenants.provision.subsystem.software'), disabled: false },
    { value: 'risk', label: this.t('platform.tenants.provision.subsystem.risk'), disabled: false },
    { value: 'admin', label: this.t('platform.tenants.provision.subsystem.adminSoon'), disabled: true },
    { value: 'sales', label: this.t('platform.tenants.provision.subsystem.salesSoon'), disabled: true },
    { value: 'insurance', label: this.t('platform.tenants.provision.subsystem.insuranceSoon'), disabled: true }
  ]);

  readonly calendarOptions = computed<SelectOption<'gregorian' | 'jalali'>[]>(() => [
    { value: 'jalali', label: this.t('localizationPreferences.calendar.jalali') },
    { value: 'gregorian', label: this.t('localizationPreferences.calendar.gregorian') }
  ]);

  readonly planOptions = computed<SelectOption[]>(() => [
    { value: 'trial-30', label: 'trial-30' },
    { value: 'starter-monthly', label: 'starter-monthly' }
  ]);

  industryPackOptions(): SelectOption[] {
    return GRC_INDUSTRY_PACK_KEYS.map(value => ({
      value,
      label: this.t(`grc.foundation.settings.industryPack.${value}`)
    }));
  }

  isMarketplace(): boolean {
    return this.model.subsystemKey === 'marketplace';
  }

  isSoftware(): boolean {
    return this.model.subsystemKey === 'software';
  }

  isStorefrontTenant(): boolean {
    return this.isMarketplace() || this.isSoftware();
  }

  private storefrontCopyKey(suffix: string): string {
    return this.isSoftware()
      ? `platform.tenants.provision.software.${suffix}`
      : `platform.tenants.provision.marketplace.${suffix}`;
  }

  pageTitle(): string {
    return this.isStorefrontTenant()
      ? this.t(this.storefrontCopyKey('title'))
      : this.t('platform.tenants.provision.title');
  }

  pageDescription(): string {
    return this.isStorefrontTenant()
      ? this.t(this.storefrontCopyKey('description'))
      : this.t('platform.tenants.provision.description');
  }

  successTitle(): string {
    return this.isStorefrontTenant()
      ? this.t(this.storefrontCopyKey('successTitle'))
      : this.t('platform.tenants.provision.success.title');
  }

  hintTitleKey(): string {
    return this.isStorefrontTenant()
      ? this.storefrontCopyKey('hint.title')
      : 'platform.tenants.provision.hint.title';
  }

  hintDescriptionKey(): string {
    return this.isStorefrontTenant()
      ? this.storefrontCopyKey('hint.description')
      : 'platform.tenants.provision.hint.description';
  }

  hintBulletKeys(): string[] {
    if (this.isStorefrontTenant()) {
      const prefix = this.isSoftware()
        ? 'platform.tenants.provision.software.hint.bullet'
        : 'platform.tenants.provision.marketplace.hint.bullet';
      return [`${prefix}1`, `${prefix}2`, `${prefix}3`, `${prefix}4`, `${prefix}5`];
    }

    return [
      'platform.tenants.provision.hint.bullet1',
      'platform.tenants.provision.hint.bullet2',
      'platform.tenants.provision.hint.bullet3',
      'platform.tenants.provision.hint.bullet4',
      'platform.tenants.provision.hint.bullet5'
    ];
  }

  canSubmit(): boolean {
    const baseReady = Boolean(
      this.model.subsystemKey.trim()
      && this.model.name.trim()
      && this.model.slug.trim()
      && this.model.legalName.trim()
      && this.model.defaultCulture.trim()
      && this.model.defaultTimeZone.trim()
      && this.model.planCode.trim()
      && this.model.adminEmail.trim()
      && this.model.adminDisplayName.trim()
    );
    if (!baseReady) {
      return false;
    }

    if (this.isStorefrontTenant()) {
      return Boolean(this.model.host.trim() && this.model.defaultCurrency.trim());
    }

    return Boolean(this.model.industryPackKey.trim());
  }

  submit(): void {
    void this.runFrom(this.plannedStepKeys()[0] ?? 'tenant_created');
  }

  retryFailed(): void {
    const failed = this.checklist().find(item => item.status === 'failed');
    void this.runFrom(failed?.key ?? this.plannedStepKeys()[0] ?? 'tenant_created');
  }

  confirmDiscard(): void {
    const tenantId = this.result()?.tenantId;
    if (!tenantId || this.busy()) {
      return;
    }

    this.confirmation.confirm({
      header: this.t('platform.tenants.provision.discardDraft'),
      message: this.t('platform.tenants.provision.discardDraftConfirm'),
      accept: () => void this.discardDraft(tenantId)
    });
  }

  copyPassword(): void {
    const password = this.temporaryPassword();
    if (!password) {
      return;
    }

    void navigator.clipboard.writeText(password).then(() => {
      this.passwordCopied.set(true);
    });
  }

  yesNo(value: boolean): string {
    return value ? this.t('common.yes') : this.t('common.no');
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  checklistTitle(): string {
    if (this.allSucceeded()) {
      return this.t('platform.tenants.provision.success.steps');
    }

    if (this.hasFailed()) {
      return this.t('platform.tenants.provision.checklist.failedTitle');
    }

    return this.t('platform.tenants.provision.checklist.title');
  }

  stepStatusLabel(status: string): string {
    return this.t(`platform.tenants.provision.stepStatus.${status}`);
  }

  formatEntitlement(item: ProvisionTenantResponse['entitlements'][number]): string {
    const limitSuffix = item.limitValue == null ? '' : ` (${item.limitKey}: ${item.limitValue})`;
    return `${item.moduleKey} / ${item.featureKey}${limitSuffix}`;
  }

  entitlementTrackKey(item: ProvisionTenantResponse['entitlements'][number]): string {
    return `${item.moduleKey}:${item.featureKey}:${item.limitKey}`;
  }

  private plannedStepKeys(): string[] {
    if (this.isStorefrontTenant()) {
      const steps = [
        'tenant_created',
        'organization_unit_created',
        'subscription_assigned',
        'identity_system_roles',
        'admin_invited',
        'storefront_site_provisioned'
      ];
      if (this.model.enablePaymentsProvider) {
        steps.push('payments_provider_enabled');
      }
      steps.push('tenant_activated');
      return steps;
    }

    const steps = [
      'tenant_created',
      'grc_industry_pack_configured',
      'subscription_assigned',
      'identity_system_roles',
      'admin_invited'
    ];
    if (this.model.activateIndustryPack) {
      steps.push('grc_pack_catalog_seeded', 'grc_industry_pack_activated', 'grc_credit_book_bootstrapped');
    }
    if (this.model.seedDemoData) {
      steps.push('grc_demo_seeded');
    }
    if (this.model.seedFccDemo) {
      steps.push('fcc_demo_seeded');
    }
    steps.push('tenant_activated');
    return steps;
  }

  private async runFrom(startStep: string): Promise<void> {
    if (!this.canSubmit() || this.busy()) {
      return;
    }

    const planned = this.plannedStepKeys();
    const startIndex = Math.max(0, planned.indexOf(startStep));
    this.checklist.set(planned.map((key, index) => ({
      key,
      status: index < startIndex ? 'succeeded' : 'pending',
      error: ''
    })));
    this.error.set(null);
    this.busy.set(true);
    this.passwordCopied.set(false);

    try {
      for (let index = startIndex; index < planned.length; index++) {
        const stepKey = planned[index]!;
        this.patchStep(stepKey, 'running');
        const response = await firstValueFrom(this.api.provisionTenant({
          ...this.buildRequest(),
          stepKey
        }));
        this.mergeResponse(response);
        if (!response.succeeded) {
          this.patchStep(response.failedStep || stepKey, 'failed', response.error ?? this.t('platform.tenants.provision.errorSubmit'));
          return;
        }

        this.patchStep(stepKey, 'succeeded');
      }
    } catch (err) {
      const current = this.checklist().find(item => item.status === 'running')?.key ?? startStep;
      const message = err instanceof HttpErrorResponse
        ? this.resolveErrorMessage(err)
        : this.t('platform.tenants.provision.errorSubmit');
      this.patchStep(current, 'failed', message);
      this.error.set(message);
    } finally {
      this.busy.set(false);
    }
  }

  private async discardDraft(tenantId: string): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    try {
      await firstValueFrom(this.api.discardDraftTenant(tenantId));
      this.result.set(null);
      this.checklist.set([]);
      this.temporaryPassword.set('');
    } catch (err) {
      this.error.set(err instanceof HttpErrorResponse
        ? this.resolveErrorMessage(err)
        : this.t('platform.tenants.provision.discardDraftFailed'));
    } finally {
      this.busy.set(false);
    }
  }

  private buildRequest(): ProvisionTenantRequest {
    return {
      subsystemKey: this.model.subsystemKey.trim(),
      name: this.model.name.trim(),
      slug: this.model.slug.trim(),
      legalName: this.model.legalName.trim(),
      defaultCulture: this.model.defaultCulture.trim(),
      defaultTimeZone: this.model.defaultTimeZone.trim(),
      defaultCalendarSystem: this.mapCalendarForApi(this.model.defaultCalendarSystem),
      industryPackKey: this.isStorefrontTenant() ? 'manufacturing' : this.model.industryPackKey.trim(),
      planCode: this.model.planCode.trim(),
      selectedUserCount: 5,
      selectedCompanyCount: 1,
      adminEmail: this.model.adminEmail.trim(),
      adminDisplayName: this.model.adminDisplayName.trim(),
      roleSystemName: 'TenantOwner',
      activateIndustryPack: this.isStorefrontTenant() ? false : this.model.activateIndustryPack,
      seedDemoData: this.isStorefrontTenant() ? false : this.model.seedDemoData,
      seedFccDemo: this.isStorefrontTenant() ? false : this.model.seedFccDemo,
      host: this.isStorefrontTenant() ? this.model.host.trim() : null,
      storeName: this.isStorefrontTenant() ? (this.model.storeName.trim() || null) : null,
      storeKey: this.isStorefrontTenant() ? (this.model.storeKey.trim() || null) : null,
      defaultCurrency: this.isStorefrontTenant() ? this.model.defaultCurrency.trim() : null,
      enablePaymentsProvider: this.isStorefrontTenant() ? this.model.enablePaymentsProvider : false
    };
  }

  private mergeResponse(response: ProvisionTenantResponse): void {
    this.result.set(response);
    if (response.temporaryPassword) {
      this.temporaryPassword.set(response.temporaryPassword);
    }

    if (!response.stepResults?.length) {
      return;
    }

    this.checklist.update(items => items.map(item => {
      const match = response.stepResults?.find(step => step.key === item.key);
      if (!match || match.status === 'pending') {
        return item;
      }

      return this.toChecklistItem(match);
    }));
  }

  private toChecklistItem(step: ProvisionStepResult): ChecklistItem {
    return {
      key: step.key,
      status: step.status,
      error: step.error?.trim() ?? ''
    };
  }

  private patchStep(key: string, status: string, error = ''): void {
    this.checklist.update(items => items.map(item => item.key === key ? { ...item, status, error } : item));
  }

  private mapCalendarForApi(value: 'gregorian' | 'jalali'): 'Gregorian' | 'Jalali' {
    return value === 'gregorian' ? 'Gregorian' : 'Jalali';
  }

  private resolveErrorMessage(err: HttpErrorResponse): string {
    const body = (err.error ?? {}) as Record<string, unknown>;
    const code = this.readString(body['Error']);
    if (code === 'tenant_slug_exists') {
      return this.t('platform.tenants.errorSlugExists');
    }
    if (code === 'host_required') {
      return this.t('platform.tenants.provision.errorHostRequired');
    }
    if (code === 'host_already_bound') {
      return this.t('platform.tenants.errorHostAlreadyBound');
    }
    if (code === 'tenant_not_draft') {
      return this.t('platform.tenants.provision.errorNotDraft');
    }
    if (code === 'bootstrap_tenant_protected') {
      return this.t('platform.tenants.provision.errorBootstrapProtected');
    }

    return this.readString(body['Message'])
      || this.readString(body['message'])
      || code
      || this.t('platform.tenants.provision.errorSubmit');
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
